import React, { useContext, useEffect } from "react";
import { Routes, Route, NavLink, useLocation } from "react-router-dom";
import {
  FiCpu,
  FiInbox,
  FiMessageSquare,
  FiUser,
  FiRefreshCw,
} from "react-icons/fi";
import styles from "./styles.module.css";
import UserProfile from "../../pages/UserProfile";
import UserStats from "../../pages/UserStats";
import UserContext from "../../store/user-context";
import UserRobots from "../UserRobots";
import UserConversations from "../UserConversations";
import { BrandLogo, Button, LoadingState, ErrorState } from "../UI";
import { TicketProvider, useTickets } from "../../store/ticket-context";
import { addRobotsToUser } from "../../utils/add-robots";
import { toast } from "react-toastify";
import { getUserGivenEmail } from "../../utils/onboarding";
type Props = { serialNumbersToBeAdded: string[]; userEmail: string | null };
const navItems = [
  { path: "/", label: "My robots", mobile: "Robots", Icon: FiCpu },
  {
    path: "/my-stats",
    label: "Service tickets",
    mobile: "Tickets",
    Icon: FiInbox,
  },
  {
    path: "/conversations",
    label: "Conversations",
    mobile: "Conversations",
    Icon: FiMessageSquare,
  },
  { path: "/my-profile", label: "Account", mobile: "Account", Icon: FiUser },
];
function Navigation({ mobile = false }: { mobile?: boolean }) {
  const location = useLocation();
  const { tickets, loading, failedRobots } = useTickets();
  const params = new URLSearchParams(location.search);
  params.delete("robot");
  const openCount = tickets.filter(
    (entry) => entry.ticket.status !== "Closed",
  ).length;
  return (
    <nav
      className={mobile ? styles.mobileNav : styles.navigation}
      aria-label={mobile ? "Mobile navigation" : "Main navigation"}
    >
      {navItems.map(({ path, label, mobile: short, Icon }) => (
        <NavLink
          key={path}
          to={{ pathname: path, search: params.toString() }}
          end={path === "/"}
          className={({ isActive }) =>
            `${styles.navItem} ${isActive || (path === "/" && location.pathname === "/my-robots") ? styles.active : ""}`
          }
        >
          <Icon size={mobile ? 22 : 20} aria-hidden="true" />
          <span>{mobile ? short : label}</span>
          {!mobile &&
            path === "/my-stats" &&
            !loading &&
            !failedRobots.length &&
            openCount > 0 && (
              <span className={styles.navCount}>{openCount}</span>
            )}
        </NavLink>
      ))}
    </nav>
  );
}
function Workspace({
  isRefreshing,
  onRefresh,
}: {
  isRefreshing: boolean;
  onRefresh: () => void;
}) {
  const { user, setUser } = useContext(UserContext);
  const location = useLocation();
  const name = user?.establishment || "Your account";
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
  return (
    <div className={styles.container}>
      <a className={styles.skipLink} href="#main-content">
        Skip to content
      </a>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <BrandLogo />
          <p>SERVICE PROGRAM</p>
        </div>
        <Navigation />
        <div className={styles.accountSummary}>
          <span className={styles.avatar}>{initials}</span>
          <div>
            <strong title={name}>{name}</strong>
            <span title={user?.email}>{user?.email}</span>
          </div>
        </div>
      </aside>
      <div className={styles.workspace}>
        <header className={styles.header}>
          <BrandLogo className={styles.mobileLogo} />
          <p>Service program</p>
          <Button
            variant="ghost"
            className={styles.refresh}
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label="Refresh dashboard"
            aria-busy={isRefreshing}
          >
            <FiRefreshCw aria-hidden="true" />
            <span>{isRefreshing ? "Refreshing…" : "Refresh"}</span>
          </Button>
        </header>
        <main
          className={styles.content}
          id="main-content"
          tabIndex={-1}
          key={location.pathname}
        >
          {!user ? (
            isRefreshing ? (
              <LoadingState label="Loading your account…" />
            ) : (
              <ErrorState
                message="Your account could not be loaded."
                onRetry={onRefresh}
              />
            )
          ) : (
            <Routes>
              <Route
                path="/"
                element={<UserRobots user={user} setUser={setUser} />}
              />
              <Route
                path="my-robots"
                element={<UserRobots user={user} setUser={setUser} />}
              />
              <Route
                path="my-profile"
                element={
                  <UserProfile
                    user={user}
                    setUser={setUser}
                    refreshProfile={onRefresh}
                  />
                }
              />
              <Route path="my-stats" element={<UserStats user={user} />} />
              <Route
                path="conversations"
                element={<UserConversations user={user} />}
              />
              <Route
                path="*"
                element={<UserRobots user={user} setUser={setUser} />}
              />
            </Routes>
          )}
        </main>
      </div>
      <Navigation mobile />
    </div>
  );
}
const Dashboard: React.FC<Props> = ({ serialNumbersToBeAdded, userEmail }) => {
  const { user, setUser } = useContext(UserContext);
  const [refresh, setRefresh] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  useEffect(() => {
    setIsRefreshing(true);
    const email = user?.email || userEmail;
    if (email) {
      getUserGivenEmail(email, !!serialNumbersToBeAdded.length)
        .then((fetchedUser) => {
          console.log("Fetched user: ", fetchedUser);
          if (fetchedUser) setUser(fetchedUser);
        })
        .catch((error) => console.error(error))
        .finally(() => setIsRefreshing(false));
    } else {
      setIsRefreshing(false);
    }
    // Keep the existing explicit refresh trigger; account updates must not start a refresh loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  useEffect(() => {
    // if there are serial numbers to be added,we add the serial numbers to the user's database entry
    setIsRefreshing(true);
    if (serialNumbersToBeAdded.length > 0 && user) {
      const alreadyRegisteredNumbers = Object.keys(user.serialNumbers || {});
      const uniqueSerialNumbersToBeAdded = serialNumbersToBeAdded.filter(
        (serialNumber) => !alreadyRegisteredNumbers.includes(serialNumber),
      );
      if (uniqueSerialNumbersToBeAdded.length === 0) {
        toast.info("Serial numbers already registered to user.");
        setIsRefreshing(false);
        return;
      } else {
        console.log(
          "Adding serial numbers to user: ",
          uniqueSerialNumbersToBeAdded,
        );
        addRobotsToUser(user.email, uniqueSerialNumbersToBeAdded)
          .then((wereAdded) => {
            if (wereAdded) {
              toast.success("Robots added successfully!");
              getUserGivenEmail(user.email, true)
                .then((fetchedUser) => {
                  if (fetchedUser) setUser(fetchedUser);
                })
                .catch((error) => {
                  console.error(error);
                })
                .finally(() => setIsRefreshing(false));
            }
          })
          .catch((error) => {
            console.error(error);
            toast.error("Error adding robots to user, please try again.");
          })
          .finally(() => setIsRefreshing(false));
      }
    } else if (!user && userEmail && serialNumbersToBeAdded.length > 0) {
      // no user found but there are serial numbers to be added -- could happen now with the new db
      addRobotsToUser(userEmail, serialNumbersToBeAdded)
        .then((wereAdded) => {
          if (wereAdded) {
            toast.success("Robots added successfully!");
            getUserGivenEmail(userEmail, true)
              .then((fetchedUser) => {
                if (fetchedUser) setUser(fetchedUser);
              })
              .catch((error) => {
                console.error(error);
              })
              .finally(() => setIsRefreshing(false));
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error("Error adding robots to user, please try again.");
        })
        .finally(() => setIsRefreshing(false));
    }
    setIsRefreshing(false);
    // Registration from the incoming link runs once when the dashboard mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TicketProvider user={user} refreshKey={refresh}>
      <Workspace
        isRefreshing={isRefreshing}
        onRefresh={() => setRefresh((value) => !value)}
      />
    </TicketProvider>
  );
};
export default Dashboard;
